import {
  Accordion,
  AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Box,
  Button, Flex, Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay, Text, useToast
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetQuestionnairesMethodologyAndSources } from "../../../../../../hooks/query/reports";
import { hideAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { showErrorToast } from "../../../../../../utils";
import { useDispatch } from "react-redux";
import FileDownloadButton from "../../../../../../components/FileDownloadButton";

function NotesModal({ isOpen, onClose, year, quarter }) {
  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "companyEmissionsDashboard.totalEmissions.notesModal"
  });
  const toast = useToast();
  const dispatch = useDispatch();

  const { data } = useGetQuestionnairesMethodologyAndSources({ year, quarter }, {
    onError: error => showErrorToast(toast, error.response?.data?.parameters?.message ?? error.message),
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  });

  const filteredData = useMemo(() => (data ?? []).filter(q =>
    (q.methodology && q.methodology.length > 0) ||
    (q.sourceFiles && q.sourceFiles.length > 0)
  ), [data]);

  const scopeLabel = (scopeKey) => {
    if (scopeKey === "scope1") return globalTranslation("scopes.scope1", "Scope 1");
    if (scopeKey === "scope2") return globalTranslation("scopes.scope2", "Scope 2");
    return globalTranslation(`dataEntryPlanning.modal.categories.${scopeKey}`, scopeKey);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("title")}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {filteredData.length > 0 ? (
            <Accordion height="500px" overflow="auto" defaultIndex={[0]} allowToggle={true}>
              {filteredData.map((questionnaire, index) => {
                const methodology = questionnaire.methodology ?? [];
                const sourceFiles = questionnaire.sourceFiles ?? [];
                return (
                  <AccordionItem key={`${questionnaire.assignee}-${index}`}>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <Heading color="esg_gray.200" size="xs">
                          <Text fontSize="md" fontWeight="bold">
                            {t("period")}: {questionnaire.period?.year ?? ""} {questionnaire.period?.quarter ?? ""}, {t("filledInBy")}: {questionnaire.assignee}, {t("dimensions")}: {!questionnaire.dimensions || questionnaire.dimensions.length === 0
                            ? t("all")
                            : questionnaire.dimensions.map(dim => `${dim.name} - ${dim.value}`).join(", ")}
                          </Text>
                        </Heading>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <Flex flexDirection="column" gap={6}>
                        {methodology.length > 0 && (
                          <Box>
                            <Text fontSize="md" fontWeight="bold" mb={2}>{t("methodology")}</Text>
                            <Flex flexDirection="column" gap={3}>
                              {methodology.map((entry, entryIndex) => (
                                <Flex
                                  key={`methodology-${index}-${entry.scopeKey}-${entryIndex}`}
                                  flexDirection="column"
                                  backgroundColor={entryIndex % 2 === 1 ? "#EDF2F7" : null}
                                  padding={3}
                                >
                                  <Text fontSize="md" fontWeight="bold" mb={1}>
                                    {scopeLabel(entry.scopeKey)}
                                  </Text>
                                  <Text fontSize="md" whiteSpace="pre-line">{entry.content}</Text>
                                </Flex>
                              ))}
                            </Flex>
                          </Box>
                        )}
                        {sourceFiles.length > 0 && (
                          <Box>
                            <Text fontSize="md" fontWeight="bold" mb={2}>{t("sourceFiles")}</Text>
                            <Flex flexDirection="column" gap={2}>
                              {sourceFiles.map((file, fileIndex) => (
                                <Flex
                                  key={`source-${index}-${file.fileId}-${fileIndex}`}
                                  alignItems="center"
                                  gap={3}
                                  padding={2}
                                  backgroundColor={fileIndex % 2 === 1 ? "#EDF2F7" : null}
                                >
                                  <Text fontSize="sm" fontWeight="bold" minWidth="200px">
                                    {scopeLabel(file.scopeKey)}
                                  </Text>
                                  <Text fontSize="sm" flex="1">{file.fileName}</Text>
                                  <FileDownloadButton
                                    fileInfo={{ documentId: file.documentId, filename: file.fileName }}
                                    width="auto"
                                    size="sm"
                                  />
                                </Flex>
                              ))}
                            </Flex>
                          </Box>
                        )}
                      </Flex>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          ) : (
            t("noQuestionnairesWithMethodology")
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {globalTranslation("buttons.close")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default NotesModal;
